/**
 * Who you look like. Five of them, and not one of them is a rule.
 *
 * Greybox means the body is a capsule, so a "preset" can only ever be a set of
 * colours — and that is deliberate rather than a limitation being made the best
 * of. The capsule's radius and height are the collision shape: every reach,
 * every doorway, every anti-tunnel guard and every measured corridor time in
 * the benchmark is a function of them. A creator that let you pick a taller
 * body would be a creator that let you pick a different game, and the first
 * person to notice would be the benchmark.
 *
 * So the rule is stated once here and enforced by a test: a preset may change
 * what colour you are and nothing else.
 *
 * Each one carries two colours rather than one, because the clothing slot has
 * to be readable at a glance from a third person camera. `blacks` is banquet
 * uniform — a mid-dark value, because a light body vanishes on the marble and a
 * black one vanishes in the service corridor. `civvies` is what you turned up
 * in: the same value range, obviously not blacks, so a player who has forgotten
 * what they changed into at the locker can see it on their own back.
 *
 * `accent` is the shirt and the facing wedge. It does not change with the
 * clothing, because it is the only cue that says which way the body is pointed
 * and a facing cue that dims when you change your trousers is a bug.
 */
export const BODIES = Object.freeze([
  { id: 'slate', label: 'Slate', blacks: 0x3c4452, civvies: 0x7a5f4a, accent: 0xe8e4dc },
  { id: 'umber', label: 'Umber', blacks: 0x473f3a, civvies: 0x8a6a3c, accent: 0xe4dcd0 },
  { id: 'moss', label: 'Moss', blacks: 0x3a4740, civvies: 0x5f7a52, accent: 0xdfe6dc },
  { id: 'rust', label: 'Rust', blacks: 0x4a3c3c, civvies: 0x8a5548, accent: 0xefdcd4 },
  { id: 'indigo', label: 'Indigo', blacks: 0x373c52, civvies: 0x4f5c8a, accent: 0xdcdff0 },
]);

export const BODY_IDS = Object.freeze(BODIES.map((body) => body.id));

export const BODIES_BY_ID = Object.freeze(
  Object.fromEntries(BODIES.map((body) => [body.id, body]))
);

export function bodyFor(id) {
  return BODIES_BY_ID[id] ?? BODIES[0];
}
